import { useConfirmModal } from '@affine/component';
import { FloatingToolbar } from '@affine/core/components/page-list/components/floating-toolbar';
import { AppSidebarService } from '@affine/core/modules/app-sidebar';
import { useI18n } from '@affine/i18n';
import { CloseIcon, DeleteIcon } from '@blocksuite/icons/rc';
import { cssVarV2 } from '@streamlain/theme/v2';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useContext, useMemo } from 'react';

import {
  NavigationPanelTreeSelectionContext,
  type NavigationPanelTreeSelectionItem,
} from './context';
import * as styles from './selection-toolbar.css';
import {
  filterNavigationPanelBlockedTrashSelectionItems,
  filterNavigationPanelTrashableSelectionItems,
} from './selection-trash-capability';
import { useNavigationPanelTrashActions } from './use-navigation-panel-trash-actions';

function getSelectionItemLabel(item: NavigationPanelTreeSelectionItem) {
  return item.label?.trim() || item.entity.id;
}

export function NavigationPanelTreeSelectionToolbar() {
  const t = useI18n();
  const selectionContext = useContext(NavigationPanelTreeSelectionContext);
  const appSidebarService = useService(AppSidebarService).sidebar;
  const sidebarWidth = useLiveData(appSidebarService.width$);
  const { trashNavigationPanelItems } = useNavigationPanelTrashActions();
  const { openConfirmModal } = useConfirmModal();

  const selectedCount = selectionContext?.selectedCount ?? 0;
  const selectedItems = useMemo(
    () => selectionContext?.selectedItems ?? [],
    [selectionContext?.selectedItems]
  );
  const trashableItems = useMemo(
    () => filterNavigationPanelTrashableSelectionItems(selectedItems),
    [selectedItems]
  );
  const blockedTrashItems = useMemo(
    () => filterNavigationPanelBlockedTrashSelectionItems(selectedItems),
    [selectedItems]
  );
  const hasDeleteCandidates = selectedItems.some(
    item => item.entity.type === 'doc' || item.entity.type === 'folder'
  );
  const toolbarWidth =
    Number.isFinite(sidebarWidth) && sidebarWidth > 0 ? sidebarWidth : 320;

  const trashSelectedItems = useCallback(
    async (items: typeof trashableItems) => {
      if (!selectionContext || !items.length) {
        return;
      }
      const completed = await trashNavigationPanelItems(items);
      if (completed !== false) {
        selectionContext.clearSelection();
      }
    },
    [selectionContext, trashNavigationPanelItems]
  );

  const handleTrashSelected = useCallback(() => {
    if (!selectionContext || !hasDeleteCandidates) {
      return;
    }

    if (blockedTrashItems.length === 0) {
      void trashSelectedItems(trashableItems);
      return;
    }

    openConfirmModal(
      {
        title:
          trashableItems.length > 0
            ? t['com.streamlain.navigation.selection.deleteBlocked.title']()
            : t[
                'com.streamlain.navigation.selection.deleteBlocked.all.title'
              ](),
        description:
          trashableItems.length > 0
            ? t[
                'com.streamlain.navigation.selection.deleteBlocked.description'
              ]()
            : t[
                'com.streamlain.navigation.selection.deleteBlocked.all.description'
              ](),
        children: (
          <div
            style={{
              display: 'grid',
              gap: 8,
              maxHeight: 220,
              overflow: 'auto',
            }}
          >
            {blockedTrashItems.map((item, index) => (
              <div key={`${item.entity.type}:${item.entity.id}:${index}`}>
                {t['com.streamlain.navigation.selection.deleteBlocked.item']({
                  name: getSelectionItemLabel(item),
                })}
              </div>
            ))}
          </div>
        ),
        confirmText:
          trashableItems.length > 0
            ? t['com.streamlain.navigation.selection.deleteBlocked.continue']()
            : t['OK'](),
        cancelText: t['Cancel'](),
        confirmButtonOptions:
          trashableItems.length > 0
            ? {
                variant: 'error',
              }
            : undefined,
        onConfirm: async () => {
          selectionContext.deselectItems(blockedTrashItems);
          await trashSelectedItems(trashableItems);
        },
      },
      {
        autoClose: true,
      }
    );
  }, [
    blockedTrashItems,
    hasDeleteCandidates,
    openConfirmModal,
    selectionContext,
    t,
    trashSelectedItems,
    trashableItems,
  ]);

  if (!selectionContext) {
    return null;
  }

  return (
    <FloatingToolbar
      className={styles.toolbarAnchor}
      contentProps={{ 'data-navigation-panel-selection-ui': true }}
      style={{ width: toolbarWidth }}
      open={selectedCount > 0}
    >
      <FloatingToolbar.Item>
        <div style={{ color: cssVarV2.text.secondary }}>
          {t['com.streamlain.m.selector.info-total']({
            total: String(selectedCount),
          })}
        </div>
      </FloatingToolbar.Item>
      <FloatingToolbar.Button
        data-testid="navigation-panel-selection-toolbar-clear"
        onClick={selectionContext.clearSelection}
        icon={<CloseIcon />}
      />
      {hasDeleteCandidates ? (
        <>
          <FloatingToolbar.Separator />
          <FloatingToolbar.Button
            data-testid="navigation-panel-selection-toolbar-trash"
            onClick={handleTrashSelected}
            icon={<DeleteIcon />}
            type="danger"
          />
        </>
      ) : null}
    </FloatingToolbar>
  );
}
